import { Bell, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ModeToggle } from "@/components/ModeToggle";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useNotifications } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";
import { useLocation } from "wouter";
import { useSubscriptionStatus } from "@/hooks/use-subscription-guard";
import type { ActivityEvent } from "@/api/generated/client";
import { getNotificationUrl, getNotificationTaskTitles, hasNotificationTaskDetails } from "@/utils/notification.utils";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

interface GlobalHeaderProps {
    onOpenMobileNav: () => void;
}

export function GlobalHeader({ onOpenMobileNav }: GlobalHeaderProps) {
    const [, navigate] = useLocation();
    const { toast } = useToast();
    
    // Check subscription status using shared hook with app-wide caching
    const { data: subscriptionStatus } = useSubscriptionStatus();

    const hasActiveSubscription = subscriptionStatus?.hasActiveSubscription === true;

    const { unreadNotifications, unreadCount, markOneAsRead, markAllAsSeen, isUnread } = useNotifications({
        limit: 20,
        // Uses default 60s polling with Page Visibility API (pauses when tab hidden)
    });

    const handleNotificationClick = async (event: ActivityEvent) => {
        // Mark as read BEFORE navigating so the context records
        // the ID even if navigation causes a component remount.
        if (isUnread(event)) {
            try {
                await markOneAsRead(event.id);
            } catch (error) {
                console.error('[GlobalHeader] Failed to mark notification as read:', error);
                // Continue with navigation even if marking as read fails
                toast({
                    title: "Failed to mark notification as read",
                    description: "The notification may still appear as unread.",
                    variant: "destructive",
                });
            }
        }

        // Navigate to the relevant page
        const url = getNotificationUrl(event);
        if (url) {
            navigate(url);
        } else {
            console.warn('[GlobalHeader] No URL to navigate to');
        }
    };

    const handleMarkAllAsSeen = async () => {
        try {
            await markAllAsSeen();
        } catch (error) {
            toast({
                title: "Failed to mark all notifications as seen",
                description: "Please try again later.",
                variant: "destructive",
            });
            console.error("Failed to mark all notifications as seen:", error);
        }
    };

    return (
        <header className="flex h-16 w-full items-center justify-between md:justify-end px-4 sm:px-6 border-b bg-background gap-4">
            {/* Mobile navigation trigger */}
            <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={onOpenMobileNav}
                aria-label="Open navigation menu"
            >
                <Menu className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-4">
                <ModeToggle />
            {/* Notifications - disabled without active subscription */}
            <Popover>
                <PopoverTrigger asChild>
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        className="relative" 
                        disabled={!hasActiveSubscription}
                    >
                        <Bell className="h-5 w-5" />
                        <span className="sr-only">Notifications</span>
                        {hasActiveSubscription && unreadCount > 0 && (
                            <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-red-600 border-2 border-background" />
                        )}
                    </Button>
                </PopoverTrigger>
                {hasActiveSubscription && (
                <PopoverContent align="end" collisionPadding={12} className="w-[calc(100vw-2rem)] max-w-sm sm:w-96 p-0">
                    <div className="flex items-center justify-between p-4 border-b">
                        <h4 className="font-semibold">Notifications</h4>
                        {unreadCount > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                                onClick={handleMarkAllAsSeen}
                            >
                                Mark all as read
                            </Button>
                        )}
                    </div>
                    
                    <ScrollArea className="h-[400px]">
                        {unreadNotifications.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">
                                <Bell className="h-12 w-12 mx-auto mb-2 opacity-20" />
                                <p>No notifications</p>
                            </div>
                        ) : (
                            <div className="divide-y">
                                {unreadNotifications.map((event) => {
                                    const taskTitles = getNotificationTaskTitles(event);
                                    const hasTaskDetails = hasNotificationTaskDetails(event);
                                    
                                    return (
                                        <button
                                            key={event.id}
                                            className="w-full text-left p-4 hover:bg-muted/50 transition-colors cursor-pointer bg-blue-50/50 dark:bg-blue-950/20"
                                            onClick={() => handleNotificationClick(event)}
                                        >
                                            <div className="flex items-start gap-3">
                                                <span className="flex h-2 w-2 translate-y-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium break-words" style={{ overflowWrap: 'anywhere' }}>
                                                        {event.message}
                                                    </p>
                                                    {hasTaskDetails && (
                                                        <ul className="mt-2 ml-4 space-y-1">
                                                            {taskTitles.map((title, idx) => (
                                                                <li key={idx} className="text-xs text-muted-foreground list-disc">
                                                                    {title}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <p className="text-xs text-muted-foreground">
                                                            {formatDistanceToNow(new Date(event.createdAt), { addSuffix: true })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </ScrollArea>
                </PopoverContent>
                )}
            </Popover>
            </div>
        </header>
    );
}
